'use client';

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { globalOverlayProps } from '../../lib/global-overlay';
import { Z_INDEX } from '../../lib/z-index';
import { SToast, type SToastProps } from './SToast';

export type SToastPosition =
  'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';

export interface SToastNotifyOptions extends Omit<SToastProps, 'onClose'> {
  /** 자동 닫힘 지연(ms). 0이면 자동 닫힘 없음 (없으면 defaultDuration) */
  duration?: number;
}

export interface SToastContainerHandle {
  create: (options: SToastNotifyOptions) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

export interface SToastContainerProps {
  position?: SToastPosition;
  maxVisible?: number;
  /** 자동 닫힘 기본 지연(ms) */
  defaultDuration?: number;
  /** 스택 z-index. 기본값(1100)은 모달 호스트(1000) 위 — 토스트는 전역 알림이라 항상 최상단 */
  zIndex?: number;
  /** 토스트가 표시될 때 (sdToastShow) */
  onShow?: (payload: { id: string }) => void;
  /** 토스트가 제거될 때 (sdToastDismiss) */
  onDismiss?: (payload: { id: string }) => void;
}

type ToastState = 'entering' | 'visible' | 'exiting';
interface Entry {
  id: string;
  options: SToastNotifyOptions;
  state: ToastState;
}

// 원본 sd-toast-container.config 상수
const GAP = 8;
const TRANSITION_DURATION = 400;
const TRANSITION_FALLBACK_BUFFER = 100;
const COLLAPSED_SCALE_STEP = 0.05;
const COLLAPSED_OFFSET_STEP = 8;

const POS_ANCHOR: Record<SToastPosition, CSSProperties> = {
  'top-left': { top: 20, left: 20 },
  'top-center': { top: 20, left: '50%' },
  'top-right': { top: 20, right: 20 },
  'bottom-left': { bottom: 100, left: 20 },
  'bottom-center': { bottom: 100, left: '50%' },
  'bottom-right': { bottom: 100, right: 20 },
};

let counter = 0;

/** SToastContainer — sd-toast-container 포팅. 스택/호버-확장 + 슬라이드 애니메이션. ref로 create/dismiss/dismissAll. */
export const SToastContainer = forwardRef<SToastContainerHandle, SToastContainerProps>(
  function SToastContainer(
    {
      position = 'bottom-center',
      maxVisible = 3,
      defaultDuration = 4000,
      zIndex = Z_INDEX.toast,
      onShow,
      onDismiss,
    },
    ref,
  ) {
    const [toasts, setToasts] = useState<Entry[]>([]);
    const [expanded, setExpanded] = useState(false);
    const [mounted, setMounted] = useState(false);
    const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
    const elRefs = useRef(new Map<string, HTMLDivElement>());
    const [heights, setHeights] = useState<Map<string, number>>(new Map());
    const onShowRef = useRef(onShow);
    const onDismissRef = useRef(onDismiss);
    onShowRef.current = onShow;
    onDismissRef.current = onDismiss;
    const liveIds = useRef(new Set<string>());

    useEffect(() => setMounted(true), []);

    const isBottom = position.startsWith('bottom');
    const alignment = position.split('-')[1] as 'left' | 'center' | 'right';

    const removeToast = useCallback((id: string) => {
      if (!liveIds.current.has(id)) return;
      liveIds.current.delete(id);
      setToasts(prev => prev.filter(t => t.id !== id));
      elRefs.current.delete(id);
      const tm = timers.current.get(id);
      if (tm) {
        clearTimeout(tm);
        timers.current.delete(id);
      }
      onDismissRef.current?.({ id });
    }, []);

    const startDismiss = useCallback(
      (id: string) => {
        const tm = timers.current.get(id);
        if (tm) {
          clearTimeout(tm);
          timers.current.delete(id);
        }
        setToasts(prev => prev.map(t => (t.id === id ? { ...t, state: 'exiting' } : t)));
        setTimeout(() => removeToast(id), TRANSITION_DURATION + TRANSITION_FALLBACK_BUFFER);
      },
      [removeToast],
    );

    // entering → visible (다음 프레임)
    useEffect(() => {
      if (!toasts.some(t => t.state === 'entering')) return;
      const raf = requestAnimationFrame(() => {
        setToasts(prev => prev.map(t => (t.state === 'entering' ? { ...t, state: 'visible' } : t)));
      });
      return () => cancelAnimationFrame(raf);
    }, [toasts]);

    // 높이 측정 (확장 모드 오프셋 계산용)
    useLayoutEffect(() => {
      let changed = false;
      const next = new Map(heights);
      toasts.forEach(t => {
        const el = elRefs.current.get(t.id);
        if (el) {
          const h = el.offsetHeight;
          if (h > 0 && next.get(t.id) !== h) {
            next.set(t.id, h);
            changed = true;
          }
        }
      });
      if (changed) setHeights(next);
    }); // 매 렌더 측정

    const scheduleAutoClose = useCallback(
      (id: string, duration: number) => {
        if (duration > 0)
          timers.current.set(
            id,
            setTimeout(() => startDismiss(id), duration),
          );
      },
      [startDismiss],
    );

    useImperativeHandle(
      ref,
      () => ({
        create: options => {
          const id = `toast-${++counter}`;
          const duration = options.duration ?? defaultDuration;
          liveIds.current.add(id);
          setToasts(prev => [...prev, { id, options, state: 'entering' }]);
          scheduleAutoClose(id, duration);
          onShowRef.current?.({ id });
          return id;
        },
        dismiss: startDismiss,
        dismissAll: () =>
          setToasts(prev => {
            prev.forEach(t => startDismiss(t.id));
            return prev;
          }),
      }),
      [defaultDuration, scheduleAutoClose, startDismiss],
    );

    useEffect(
      () => () => {
        timers.current.forEach(t => clearTimeout(t));
      },
      [],
    );

    // maxVisible 초과 시 가장 오래된 토스트부터 자동 퇴장
    useEffect(() => {
      const activeIds = toasts.filter(t => t.state !== 'exiting').map(t => t.id);
      if (activeIds.length > maxVisible) {
        activeIds.slice(0, activeIds.length - maxVisible).forEach(id => startDismiss(id));
      }
    }, [toasts, maxVisible, startDismiss]);

    const pauseTimers = () => {
      timers.current.forEach(t => clearTimeout(t));
      timers.current.clear();
    };
    const resumeTimers = () => {
      if (defaultDuration <= 0) return;
      toasts.forEach(t => {
        if (t.state === 'visible' && !timers.current.has(t.id)) {
          const d = t.options.duration ?? defaultDuration;
          if (d > 0)
            timers.current.set(
              t.id,
              setTimeout(() => startDismiss(t.id), d),
            );
        }
      });
    };

    if (!mounted) return null;

    // 활성 토스트(퇴장 제외), 최신이 앞(front, index 0)
    const active = toasts
      .filter(t => t.state !== 'exiting')
      .slice()
      .reverse();
    const frontIndexOf = (id: string) => active.findIndex(t => t.id === id);

    const anchorProp: 'top' | 'bottom' = isBottom ? 'bottom' : 'top';
    const alignStyle: CSSProperties =
      alignment === 'center' ? { left: '50%' } : alignment === 'right' ? { right: 0 } : { left: 0 };
    const centerX = alignment === 'center' ? 'translateX(-50%) ' : '';

    const itemStyle = (entry: Entry): CSSProperties => {
      const base: CSSProperties = {
        position: 'absolute',
        [anchorProp]: 0,
        ...alignStyle,
        pointerEvents: 'auto',
      };
      const enterY = isBottom ? 20 : -20;

      if (entry.state === 'entering')
        return {
          ...base,
          opacity: 0,
          transform: `${centerX}translateY(${enterY}px)`,
          transition: 'none',
        };
      if (entry.state === 'exiting')
        return {
          ...base,
          opacity: 0,
          transform: `${centerX}translateY(${enterY}px)`,
          transition: `transform .4s ease, opacity .4s ease`,
          pointerEvents: 'none',
        };

      const fi = frontIndexOf(entry.id);
      const transition = `transform .4s ease, opacity .4s ease, ${anchorProp} .4s ease`;

      if (expanded) {
        let offset = 0;
        for (let i = 0; i < fi; i++) offset += (heights.get(active[i].id) || 0) + GAP;
        return {
          ...base,
          [anchorProp]: offset,
          opacity: 1,
          transform: `${centerX}translateY(0) scale(1)`,
          zIndex: active.length - fi,
          transition,
        };
      }

      // collapsed(스택)
      const peek = fi * COLLAPSED_OFFSET_STEP;
      const peekY = isBottom ? -peek : peek;
      const scale = 1 - fi * COLLAPSED_SCALE_STEP;
      const opacity = [1, 0.8, 0.6][fi] ?? 0;
      return {
        ...base,
        opacity,
        transform: `${centerX}translateY(${peekY}px) scale(${scale})`,
        transformOrigin: isBottom ? 'bottom center' : 'top center',
        zIndex: active.length - fi,
        transition,
      };
    };

    return createPortal(
      <div
        {...globalOverlayProps}
        style={{
          position: 'fixed',
          zIndex,
          pointerEvents: 'none',
          ...POS_ANCHOR[position],
          ...(alignment === 'center' ? { transform: 'translateX(-50%)' } : {}),
        }}
        onMouseEnter={() => {
          setExpanded(true);
          pauseTimers();
        }}
        onMouseLeave={() => {
          setExpanded(false);
          resumeTimers();
        }}
      >
        {toasts.map(entry => (
          <div
            key={entry.id}
            ref={el => {
              if (el) elRefs.current.set(entry.id, el);
            }}
            style={itemStyle(entry)}
          >
            <SToast {...entry.options} onClose={() => startDismiss(entry.id)} />
          </div>
        ))}
      </div>,
      document.body,
    );
  },
);
